import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../../../../../../../@core/services/toast.service';
import { ScoringService } from '../../../../../../../../../../@shared/services/scoring.service';
import {
  EventClassResponse,
  ClassRoundResponse
} from '../../../../../../../../../../@shared/models/response.module';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-rounds-config-modal',
  standalone: false,
  templateUrl: './rounds-config-modal.html',
  styleUrl: './rounds-config-modal.css'
})
export class RoundsConfigModal implements OnInit {
  @Input() eventClass: EventClassResponse;

  rounds: ClassRoundResponse[] = [];

  form: FormGroup;
  loading = true;
  saving = false;
  editingRound: ClassRoundResponse | null = null;

  constructor(
    private fb: FormBuilder,
    private modal: NgbActiveModal,
    private scoringService: ScoringService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    this.form = this.fb.group({
      round_name: ['', [Validators.maxLength(100)]]
    });
  }

  ngOnInit() {
    this.loadRounds();
  }

  async loadRounds() {
    this.loading = true;
    try {
      this.rounds = await firstValueFrom(
        this.scoringService.getRounds({ event_class_id: this.eventClass.event_class_id })
      ) || [];
    } catch (error) {
      this.rounds = [];
    } finally {
      this.loading = false;
    }
  }

  getNextRoundNumber(): number {
    if (this.rounds.length === 0) return 1;
    return Math.max(...this.rounds.map(r => r.round_number)) + 1;
  }

  startEdit(round: ClassRoundResponse) {
    this.editingRound = round;
    this.form.patchValue({
      round_name: round.round_name || ''
    });
  }

  cancelEdit() {
    this.editingRound = null;
    this.form.reset({ round_name: '' });
  }

  async addRound() {
    if (this.saving) return;

    this.saving = true;
    try {
      const nextNumber = this.getNextRoundNumber();
      await firstValueFrom(
        this.scoringService.createRound({
          event_class_id: this.eventClass.event_class_id,
          round_number: nextNumber,
          round_name: `Round ${nextNumber}`
        })
      );
      this.toastService.success(this.translateService.instant('rounds.savedSuccess'));
      await this.loadRounds();
    } catch (error) {
      console.error('Error adding round:', error);
      this.toastService.error(this.translateService.instant('penalties.saveError'));
    } finally {
      this.saving = false;
    }
  }

  async saveRound() {
    if (!this.editingRound || this.saving) return;

    this.saving = true;
    try {
      const formValue = this.form.value;
      await firstValueFrom(
        this.scoringService.updateRound({
          round_id: this.editingRound.round_id,
          round_name: formValue.round_name || `Round ${this.editingRound.round_number}`
        })
      );
      this.toastService.success(this.translateService.instant('rounds.savedSuccess'));
      this.cancelEdit();
      await this.loadRounds();
    } catch (error) {
      console.error('Error saving round:', error);
      this.toastService.error(this.translateService.instant('penalties.saveError'));
    } finally {
      this.saving = false;
    }
  }

  async deleteRound(round: ClassRoundResponse) {
    try {
      await firstValueFrom(
        this.scoringService.deleteRound({ round_id: round.round_id })
      );
      this.toastService.success(this.translateService.instant('rounds.deleteSuccess'));
      await this.loadRounds();
    } catch (error) {
      console.error('Error deleting round:', error);
      this.toastService.error(this.translateService.instant('penalties.saveError'));
    }
  }

  getRoundStatusClass(round: ClassRoundResponse): string {
    switch (round.status) {
      case 'completed': return 'badge-success';
      case 'in_progress': return 'badge-warning';
      default: return 'badge-secondary';
    }
  }

  getRoundStatusLabel(round: ClassRoundResponse): string {
    switch (round.status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      default: return 'Pending';
    }
  }

  close() {
    this.modal.close(this.rounds.length > 0);
  }
}
